import React, { useEffect, useState } from 'react';
import { Colors, Text, TouchableOpacity, View } from 'react-native-ui-lib';
import { ScrollView } from 'react-native-gesture-handler';
import { MaterialIcons } from '@expo/vector-icons';
import { ContainerData, getAncestorChain } from '../dataModel';
import { ElementPath } from '../typings/types';
import {
  convertInfoToPath,
  convertInfoToRouteParams,
  getBreadcrumbLabel,
  selectScreenFromInfo,
} from '../helperFunctions';
import { strings } from '../localization/localization.web';

export type BreadcrumbItem = {
  info: ElementPath;
  label: string;
};

type StatementBreadcrumbsProps = {
  navigation: any;
  container: ContainerData;
  groupId?: string;
  memberId?: string;
};

/**
 * Shows the ancestor trail (topic / statement / argument / ... ) above a
 * nested statement, so the person can see where the statement they're
 * looking at sits in the wider discussion and jump back to any ancestor.
 * Renders nothing for a top-level statement or while the trail is loading.
 */
export function StatementBreadcrumbs({
  navigation,
  container,
  groupId,
  memberId,
}: StatementBreadcrumbsProps): JSX.Element | null {
  const [items, setItems] = useState<BreadcrumbItem[]>([]);
  const containerPath = container?.getPath();
  const hasParent = !!container && (container.getParentPaths()?.length || 0) > 0;

  useEffect(() => {
    let cancelled = false;

    if (!hasParent) {
      setItems([]);
      return undefined;
    }

    getAncestorChain(container, groupId)
      .then(chain => {
        if (cancelled) {
          return;
        }
        setItems(
          chain.map(({ info, container: ancestor }) => ({
            info,
            label: getBreadcrumbLabel(ancestor.getType(), ancestor.getData() as any, strings),
          })),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerPath, groupId, hasParent]);

  if (items.length === 0) {
    return null;
  }

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 8 }}>
      <View row centerV>
        {items.map((item, index) => (
          <View row centerV key={`${convertInfoToPath(item.info)}-${index}`}>
            <TouchableOpacity
              onPress={() =>
                navigation.push(selectScreenFromInfo(item.info), convertInfoToRouteParams(item.info, memberId))
              }
            >
              <Text p textSecondary numberOfLines={1} style={{ maxWidth: 160 }}>
                {item.label}
              </Text>
            </TouchableOpacity>
            <View marginH-4>
              <MaterialIcons name="chevron-right" size={16} color={Colors.iconSecondary} />
            </View>
          </View>
        ))}
      </View>
    </ScrollView>
  );
}
